export interface CryptoQuote {
  price: number;
  volume_24h: number;
  volume_change_24h: number;
  cex_volume_24h?: number;
  dex_volume_24h?: number;
  percent_change_1h: number;
  percent_change_24h: number;
  percent_change_7d: number;
  percent_change_30d: number;
  percent_change_60d: number;
  percent_change_90d: number;
  market_cap: number;
  market_cap_dominance: number;
  fully_diluted_market_cap: number;
  tvl?: number | null;
  last_updated: string;
}

export interface CryptoData {
  _id?: string;
  id?: number;
  cmc_id?: number;
  name: string;
  symbol: string;
  logo?: string;
  description?: string;
  quote: {
    [currencyCode: string]: CryptoQuote;
  };
  circulating_supply?: number;
  total_supply?: number;
  max_supply?: number;
  isLoanAvailable?: boolean;
  isAddedToWatchList?: boolean;
  [key: string]: any;
}

/**
 * Extracts quote data for a specific currency from crypto data
 * @param cryptoData - The crypto data object from API
 * @param currencyCode - The currency code (e.g., 'USD', 'AUD', 'EUR')
 * @returns CryptoQuote object or null if not found
 */
export const extractQuoteByurrency = (
  cryptoData: CryptoData | null | undefined,
  currencyCode: string
): CryptoQuote | null => {
  if (!cryptoData?.quote) {
    console.warn(`No quote data available for currency: ${currencyCode}`);
    return null;
  }

  const quote = cryptoData.quote[currencyCode];
  
  if (!quote) {
    console.warn(`Quote not found for currency: ${currencyCode}`);
    return null;
  }

  return quote;
};

/**
 * Gets quote with fallback to first available currency
 * Useful when preferred currency is not available
 * @param cryptoData - The crypto data object from API
 * @param preferredCurrencyCode - The preferred currency code
 * @returns CryptoQuote object or first available quote or null
 */
export const extractQuoteWithFallback = (
  cryptoData: CryptoData | null | undefined,
  preferredCurrencyCode: string
): CryptoQuote | null => {
  if (!cryptoData?.quote) {
    return null;
  }

  // Try preferred currency first
  if (cryptoData.quote[preferredCurrencyCode]) {
    return cryptoData.quote[preferredCurrencyCode];
  }

  // Fallback to first available currency
  const availableCurrencies = Object.keys(cryptoData.quote);
  if (availableCurrencies.length > 0) {
    console.warn(
      `Preferred currency ${preferredCurrencyCode} not available. Falling back to ${availableCurrencies[0]}`
    );
    return cryptoData.quote[availableCurrencies[0]];
  }

  return null;
};

/**
 * Gets all available currency codes from crypto data
 * @param cryptoData - The crypto data object from API
 * @returns Array of available currency codes
 */
export const getAvailableCurrencies = (
  cryptoData: CryptoData | null | undefined
): string[] => {
  if (!cryptoData?.quote) {
    return [];
  }
  return Object.keys(cryptoData.quote);
};

/**
 * Extracts multiple crypto items with dynamic quotes
 * Handles both single items and arrays
 * @param data - API response data (single crypto or array)
 * @param currencyCode - The currency code
 * @returns Array of crypto data with quotes
 */
export const extractCryptoDataWithQuotes = (
  data: CryptoData | CryptoData[] | null | undefined,
  currencyCode: string
): (CryptoData & { currentQuote: CryptoQuote | null })[] => {
  if (!data) {
    return [];
  }

  const items = Array.isArray(data) ? data : [data];

  return items.map((item) => ({
    ...item,
    currentQuote: extractQuoteByurrency(item, currencyCode),
  }));
};

/**
 * Validates if crypto data has required quote
 * @param cryptoData - The crypto data object
 * @param currencyCode - The currency code to validate
 * @returns boolean
 */
export const hasCurrencyQuote = (
  cryptoData: CryptoData | null | undefined,
  currencyCode: string
): boolean => {
  return !!(cryptoData?.quote && cryptoData.quote[currencyCode]);
};
